// API Base URL - depuis les variables d'environnement
import API_BASE_URL from '../config/api';
import i18n from '../i18n';

/** Détail d'erreur FastAPI (string | array validation | object) → message lisible */
function formatApiErrorDetail(errorData, status) {
  const d = errorData?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => (typeof e === 'string' ? e : e?.msg || e?.message || JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (d && typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return `Erreur HTTP ${status}`;
}

// Helper function for API calls
export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  };
  // Langue du site : le serveur la mémorise sur le compte pour envoyer les emails dans cette langue
  defaultOptions.headers = { 'Accept-Language': i18n.language, ...defaultOptions.headers };

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Gestion spécifique des erreurs d'authentification
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        // Déclencher un événement personnalisé pour notifier les composants
        window.dispatchEvent(new CustomEvent('tokenRemoved'));
        
        throw new Error('Token expiré ou invalide');
      }
      
      throw new Error(formatApiErrorDetail(errorData, response.status));
    }
    
    const text = await response.text();
    if (!text || !text.trim()) return {};
    return JSON.parse(text);
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Authentication helpers
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// ==================== RESTAURANT ROUTES ====================

// Get all restaurants
export const getRestaurants = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/restaurants/?${queryParams}`);
};

export const getNearbyRestaurants = async (params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
  const queryParams = new URLSearchParams(cleanParams).toString();
  return apiCall(`/restaurants/nearby${queryParams ? `?${queryParams}` : ''}`);
};

// Get restaurant by ID with full details
export const getRestaurantById = async (restaurantId) => {
  return apiCall(`/restaurants/${restaurantId}`);
};

// Get restaurant details (profile, images, hours)
export const getRestaurantDetails = async (restaurantId) => {
  return apiCall(`/restaurants/${restaurantId}/details`);
};

// Get restaurant images
export const getRestaurantImages = async (restaurantId) => {
  return apiCall(`/restaurants/${restaurantId}/images`);
};

// Get restaurant hours
export const getRestaurantHours = async (restaurantId) => {
  return apiCall(`/restaurants/${restaurantId}/hours`);
};

// Get similar restaurants
export const getSimilarRestaurants = async (restaurantId) => {
  return apiCall(`/restaurants/${restaurantId}/similar`);
};

// Get restaurant categories
export const getRestaurantCategories = async (restaurantId) => {
  return apiCall(`/restaurants/${restaurantId}/categories`);
};

// Search restaurants
export const searchRestaurants = async (query, filters = {}, requestOptions = {}) => {
  const params = { q: query, ...filters };
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/restaurants/search?${queryParams}`, requestOptions);
};

// ==================== MENU ROUTES ====================

// Get all menus (public)
export const getAllMenus = async () => {
  return apiCall(`/menus/`);
};

// Get menus by restaurant
export const getMenusByRestaurant = async (restaurantId) => {
  return apiCall(`/menus/restaurant/${restaurantId}`);
};

// Get menu by ID
export const getMenuById = async (menuId) => {
  return apiCall(`/menus/${menuId}`);
};

// Get menu dishes
export const getMenuDishes = async (menuId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/menus/${menuId}/dishes?${queryParams}`);
};

// Get menu statistics
export const getMenuStats = async (menuId) => {
  return apiCall(`/menus/${menuId}/stats`);
};

// Get restaurant menus
export const getRestaurantMenus = async (restaurantId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/menus/restaurant/${restaurantId}?${queryParams}`);
};

// ==================== DISH ROUTES ====================

// Get all dishes
export const getDishes = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/dishes/?${queryParams}`);
};
export const getDishDetails = async (dishId) => {
  return apiCall(`/dishes/${dishId}/details`);
};
// Get dish by ID
export const getDishById = async (dishId) => {
  return apiCall(`/dishes/${dishId}`);
};

export const getDishComments = async (dishId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/comments/dish/${dishId}${queryParams ? `?${queryParams}` : ''}`);
};

export const createDishComment = async (payload) => {
  return apiCall('/comments/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

// Get dishes by restaurant
export const getDishesByRestaurant = async (restaurantId, forCatering = false) => {
  const qs = forCatering ? '?for_catering=true' : '';
  return apiCall(`/dishes/restaurant/${restaurantId}${qs}`);
};

// Get dishes by category
export const getDishesByCategory = async (categoryId) => {
  return apiCall(`/dishes/by_category/${categoryId}`);
};

// Get similar dishes
export const getSimilarDishes = async (dishId) => {
  return apiCall(`/dishes/similar/${dishId}`);
};

// Search dishes
export const searchDishes = async (query, filters = {}, requestOptions = {}) => {
  const params = { q: query, ...filters };
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/dishes/search?${queryParams}`, requestOptions);
};

// Get dish supplements
export const getDishSupplements = async (dishId) => {
  return apiCall(`/dishes/dishes/${dishId}/supplements`);
};

// ==================== REVIEWS ROUTES ====================

// Get restaurant reviews
export const getRestaurantReviews = async (restaurantId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/restaurants/${restaurantId}/reviews?${queryParams}`);
};

// Get dish reviews (public)
export const getDishReviews = async (dishId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/client-reviews/public/dishes/${dishId}?${queryParams}`);
};

// Get restaurant reviews (public)
export const getRestaurantReviewsPublic = async (restaurantId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/client-reviews/public/restaurants/${restaurantId}?${queryParams}`);
};

// Create client review
export const createClientReview = async (reviewData) => {
  return apiCall('/client-reviews/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reviewData),
  });
};

// Create restaurant review (reviewData = { rating, comment })
export const createRestaurantReview = async (restaurantId, reviewData) => {
  return apiCall('/client-reviews/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      review_type: 'restaurant',
      reviewed_object_id: Number(restaurantId),
      rating: reviewData.rating,
      comment: reviewData.comment || ''
    }),
  });
};

// Create menu review (reviewData = { rating, comment })
export const createMenuReview = async (menuId, reviewData) => {
  return apiCall('/client-reviews/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      review_type: 'menu',
      reviewed_object_id: Number(menuId),
      rating: reviewData.rating,
      comment: reviewData.comment || ''
    }),
  });
};

// Get user reviews
export const getUserReviews = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/client-reviews/?${queryParams}`, {
    headers: getAuthHeaders(),
  });
};

// Update client review
export const updateClientReview = async (reviewId, updateData) => {
  return apiCall(`/client-reviews/${reviewId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData),
  });
};

// Delete client review
export const deleteClientReview = async (reviewId) => {
  return apiCall(`/client-reviews/${reviewId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// ==================== COMPLAINT ROUTES ====================

export const createComplaint = async (complaintData) => {
  return apiCall('/complaints/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(complaintData),
  });
};

// ==================== FAVORITES ROUTES ====================

// Get user favorites
export const getUserFavorites = async () => {
  return apiCall('/favoris/', {
    headers: getAuthHeaders(),
  });
};

// Add to favorites
export const addToFavorites = async (favoriteData) => {
  return apiCall('/favoris/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(favoriteData),
  });
};

// Remove from favorites
export const removeFromFavorites = async (favoriteId) => {
  return apiCall(`/favoris/${favoriteId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

// Check if item is favorited
export const checkFavoriteStatus = async (objectId, objectType) => {
  try {
    const response = await apiCall(`/favoris/check/${objectType}/${objectId}`, {
      headers: getAuthHeaders(),
    });
    return response != null && response.is_favorite === true;
  } catch (error) {
    // Si l'erreur est d'authentification, retourner false silencieusement
    if (error.message === 'Token expiré ou invalide') {
      return false;
    }
    // Pour les autres erreurs, les relancer
    throw error;
  }
};

// ==================== CATEGORIES ROUTES ====================

// Get all categories
export const getCategories = async () => {
  return apiCall('/categories/');
};

// Get category by ID
export const getCategoryById = async (categoryId) => {
  return apiCall(`/categories/${categoryId}`);
};

// ==================== STRIPE PAYMENT ROUTES ====================

// Create payment intent
export const createPaymentIntent = async (paymentData) => {
  return apiCall('/stripe/create-payment-intent', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(paymentData),
  });
};

// Get user payments
export const getUserPayments = async () => {
  return apiCall('/stripe/payments', {
    headers: getAuthHeaders(),
  });
};

// Get payment details
export const getPaymentDetails = async (paymentId) => {
  return apiCall(`/stripe/payments/${paymentId}`, {
    headers: getAuthHeaders(),
  });
};

// ==================== ORDER ROUTES ====================

// Create order
export const createOrder = async (orderData) => {
  return apiCall('/orders/', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });
};

// Create guest order
export const createGuestOrder = async (orderData) => {
  return apiCall('/orders/guest', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });
};

export const createCashOnDeliveryOrder = async (orderData) => {
  return apiCall('/orders/cash-on-delivery', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });
};

// Get user orders
export const getUserOrders = async () => {
  return apiCall('/orders/my', {
    headers: getAuthHeaders(),
  });
};

// Get order details
export const getOrderDetails = async (orderId) => {
  return apiCall(`/orders/${orderId}`, {
    headers: getAuthHeaders(),
  });
};

// ==================== WALLET ROUTES ====================

// Get restaurant wallet
export const getRestaurantWallet = async (restaurantId) => {
  return apiCall(`/wallet/restaurant/${restaurantId}`, {
    headers: getAuthHeaders(),
  });
};

// Get wallet transactions
export const getWalletTransactions = async (restaurantId) => {
  return apiCall(`/wallet/restaurant/${restaurantId}/transactions`, {
    headers: getAuthHeaders(),
  });
};

// Withdraw from wallet
export const withdrawFromWallet = async (restaurantId, amount, description = "Retrait") => {
  return apiCall(`/wallet/restaurant/${restaurantId}/withdraw`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ amount, description }),
  });
};

// ==================== AUTHENTICATION ROUTES ====================

// Login
export const login = async (credentials) => {
  const formData = new URLSearchParams();
  formData.append('username', credentials.email);
  formData.append('password', credentials.password);
  
  return apiCall('/users/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });
};

// Register client
export const register = async (userData) => {
  return apiCall('/users/create/client', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });
};

// Register restaurant
export const registerRestaurant = async (restaurantData) => {
  return apiCall('/restaurants/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(restaurantData),
  });
};

/** Pays / villes / types de cuisine (inscription restaurateur, données admin) */
export const getReferenceCountries = async () => {
  return apiCall('/reference/countries?active_only=true');
};

export const getReferenceCities = async (countryId) => {
  return apiCall(`/reference/countries/${countryId}/cities?active_only=true`);
};

export const getReferenceCuisineTypes = async () => {
  return apiCall('/reference/cuisine-types?active_only=true');
};

// Get current user
export const getCurrentUser = async () => {
  return apiCall('/users/me', {
    headers: getAuthHeaders(),
  });
};

export const getLoyaltyAccount = async () => {
  return apiCall('/loyalty/account', {
    headers: getAuthHeaders(),
  });
};

export const getLoyaltyPointsHistory = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return apiCall(`/loyalty/points/history${queryParams ? `?${queryParams}` : ''}`, {
    headers: getAuthHeaders(),
  });
};

// ==================== STRIPE CONNECT ROUTES ====================

// Create Stripe Connect account
export const createStripeConnectAccount = async (restaurantId) => {
  return apiCall('/stripe-connect/accounts', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ restaurant_id: restaurantId }),
  });
};

// Get Stripe Connect account
export const getStripeConnectAccount = async (restaurantId) => {
  return apiCall(`/stripe-connect/accounts/${restaurantId}`, {
    headers: getAuthHeaders(),
  });
};

// Create account link for onboarding
export const createAccountLink = async (restaurantId, refreshUrl, returnUrl) => {
  return apiCall(`/stripe-connect/accounts/${restaurantId}/link`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      restaurant_id: restaurantId,
      refresh_url: refreshUrl,
      return_url: returnUrl,
    }),
  });
};

// Get account status
export const getAccountStatus = async (restaurantId) => {
  return apiCall(`/stripe-connect/accounts/${restaurantId}/status`, {
    headers: getAuthHeaders(),
  });
};

// Create Connect payment intent
export const createConnectPaymentIntent = async (orderId, amount, applicationFee) => {
  return apiCall('/stripe-connect/payment-intents', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      order_id: orderId,
      amount: amount,
      application_fee: applicationFee,
    }),
  });
};

// Get restaurant transfers
export const getRestaurantTransfers = async (restaurantId) => {
  return apiCall(`/stripe-connect/transfers/${restaurantId}`, {
    headers: getAuthHeaders(),
  });
};

// Get Stripe Connect balance for restaurant
export const getConnectBalance = async (restaurantId) => {
  return apiCall(`/stripe-connect/balance/${restaurantId}`, {
    headers: getAuthHeaders(),
  });
};

// Get Stripe Connect payouts history for restaurant
export const getConnectPayouts = async (restaurantId, limit = 50) => {
  return apiCall(`/stripe-connect/payouts/${restaurantId}?limit=${encodeURIComponent(limit)}`, {
    headers: getAuthHeaders(),
  });
};

// Request payout from Stripe Connect account to bank account
export const createConnectPayout = async (restaurantId, amount) => {
  return apiCall(`/stripe-connect/payouts/${restaurantId}`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      restaurant_id: restaurantId,
      amount,
      currency: 'eur',
      status: 'pending',
    }),
  });
};

// Request full withdrawal flow (transfer + payout)
export const requestConnectWithdrawal = async (restaurantId, amount, reason = 'Retrait de fonds') => {
  return apiCall(`/stripe-connect/withdraw/${restaurantId}`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      amount,
      reason,
    }),
  });
};

// ==================== PROFILE ROUTES ====================

// Update client profile
export const updateClientProfile = async (profileData) => {
  return apiCall('/profiles/client', {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(profileData),
  });
};

// ==================== UTILITY FUNCTIONS ====================

// Format price
export const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
};

// Format rating
export const formatRating = (rating) => {
  return rating.toFixed(1);
};

// Format date
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Get image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') return null;
  const trimmed = imagePath.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  // Chemin relatif commençant par / (ex: /media/...) → base URL + path
  if (trimmed.startsWith('/')) return `${API_BASE_URL.replace(/\/$/, '')}${trimmed}`;
  return `${API_BASE_URL}/static/${trimmed}`;
};

export const getJpegFallbackUrl = (imagePath) => {
  const absolute = getImageUrl(imagePath);
  if (!absolute) return null;
  if (/\.webp(\?.*)?$/i.test(absolute)) {
    return absolute.replace(/\.webp(\?.*)?$/i, '.jpg$1');
  }
  return null;
};

export const getImageVariantUrl = (imagePath, variant = 'original', format = 'webp') => {
  const absolute = getImageUrl(imagePath);
  if (!absolute || typeof absolute !== 'string') return null;
  const normalizedVariant = String(variant || 'original').toLowerCase();
  const normalizedFormat = String(format || 'webp').toLowerCase() === 'jpeg' ? 'jpg' : 'webp';
  if (!/_original\.(webp|jpg|jpeg)(\?.*)?$/i.test(absolute)) return absolute;
  return absolute.replace(/_original\.(webp|jpg|jpeg)(\?.*)?$/i, `_${normalizedVariant}.${normalizedFormat}$2`);
};

const firstImageByType = (imagesByType, types = []) => {
  if (!imagesByType || typeof imagesByType !== 'object') return null;
  for (const type of types) {
    const arr = imagesByType[type];
    if (Array.isArray(arr) && arr.length > 0) {
      const candidate = arr[0]?.image_url;
      if (typeof candidate === 'string' && candidate.trim()) return candidate;
    }
  }
  return null;
};

export const resolveRestaurantImagePath = (restaurant) => {
  if (!restaurant || typeof restaurant !== 'object') return null;
  const profile = restaurant.profile || {};
  const root = restaurant || {};
  const imagesByType = profile.images_by_type || root.images_by_type || {};
  return (
    root.restaurant_image_url ||
    root.image_url ||
    root.image ||
    root.card_image_url ||
    root.banner_image_url ||
    root.banner_url ||
    root.logo_url ||
    profile.restaurant_image_url ||
    profile.image_url ||
    profile.image ||
    profile.card_image_url ||
    profile.banner_image_url ||
    profile.banner_url ||
    profile.logo_url ||
    firstImageByType(imagesByType, ['restaurant', 'card', 'banner', 'slide', 'logo']) ||
    null
  );
};

export const resolveDishImagePath = (dish) => {
  if (!dish || typeof dish !== 'object') return null;
  return (
    dish.dish_image_url ||
    dish.image_url ||
    dish.imageUrl ||
    dish.image ||
    null
  );
};

/**
 * Retourne l'URL d'affichage de la bannière d'un restaurant (priorité bannière puis logo).
 * À utiliser pour les cartes et listes restaurants.
 */
export const getRestaurantBannerUrl = (restaurant, variant = 'large') => {
  const raw = resolveRestaurantImagePath(restaurant);
  if (!raw || typeof raw !== 'string' || !raw.trim()) return null;
  return getImageVariantUrl(raw, variant, 'webp') || getImageUrl(raw);
};

export const getDishImageUrl = (dish, variant = 'medium') => {
  const raw = resolveDishImagePath(dish);
  if (!raw || typeof raw !== 'string' || !raw.trim()) return null;
  return getImageVariantUrl(raw, variant, 'webp') || getImageUrl(raw);
};

export const getClientProfile = async () => {
  return apiCall('/profiles/client', {
    headers: getAuthHeaders(),
  });
};

// Error handler
export const handleApiError = (error) => {
  console.error('API Error:', error);
  return {
    error: true,
    message: error.message || 'Une erreur est survenue',
  };
};

export default {
  // Restaurant routes
  getRestaurants,
  getNearbyRestaurants,
  getRestaurantById,
  getRestaurantDetails,
  getRestaurantImages,
  getRestaurantHours,
  getSimilarRestaurants,
  getRestaurantCategories,
  searchRestaurants,
  
  // Dish routes
  getDishes,
  getDishById,
  getDishComments,
  createDishComment,
  getDishesByRestaurant,
  getDishesByCategory,
  getSimilarDishes,
  searchDishes,
  getDishSupplements,
  
  // Menu & Submenu routes
  getMenusByRestaurant,
  getMenuById,
  getMenuDishes,
  getMenuStats,
  getRestaurantMenus,
  
  // Review routes
  getRestaurantReviews,
  getDishReviews,
  getRestaurantReviewsPublic,
  createClientReview,
  createRestaurantReview,
  createComplaint,
  getUserReviews,
  updateClientReview,
  deleteClientReview,
  
  // Favorite routes
  getUserFavorites,
  addToFavorites,
  removeFromFavorites,
  checkFavoriteStatus,
  
  // Category routes
  getCategories,
  getCategoryById,
  
  // Auth routes
  login,
  register,
  registerRestaurant,
  getCurrentUser,
  getLoyaltyAccount,
  getLoyaltyPointsHistory,
  
  // Stripe Connect routes
  createStripeConnectAccount,
  getStripeConnectAccount,
  createAccountLink,
  getAccountStatus,
  createConnectPaymentIntent,
  getRestaurantTransfers,
  getConnectBalance,
  getConnectPayouts,
  createConnectPayout,
  requestConnectWithdrawal,
  
  // Profile routes
  updateClientProfile,
  getClientProfile,
  
  // Utility functions
  formatPrice,
  formatRating,
  formatDate,
  getImageUrl,
  getDishImageUrl,
  getImageVariantUrl,
  getJpegFallbackUrl,
  getRestaurantBannerUrl,
  resolveDishImagePath,
  resolveRestaurantImagePath,
  handleApiError,
}; 